import { useCallback, useEffect, useRef } from 'react';
import type React from 'react';

import { Button } from '@/routes/_embaUserLayout/-components/Button';
import { LessonTitle } from '@/routes/_embaUserLayout/-components/LessonTitle';

import VimeoPlayer from '@vimeo/player';
import type { Options } from '@vimeo/player';

import BackgroundOfficeImage from '@/assets/images/office.jpg';
import styles from './index.module.scss';

const playerDefaultOptions: Options = {
  autopause: false,
  autoplay: false,
  byline: false,
  portrait: false,
  responsive: true,
  speed: true,
  title: false,
};

export type CourseLessonMoviePageProps = {
  lessonName: string;
  chapterName: string;
  vimeoEmbedUrl: string;
  onLessonStartEvent: () => Promise<void>;
  onLessonCompleteEvent: () => Promise<void>;
  onClickDownload: () => void;
};

export const CourseLessonMoviePageComponent = ({
  lessonName,
  chapterName,
  vimeoEmbedUrl,
  onClickDownload,
  onLessonStartEvent,
  onLessonCompleteEvent,
}: CourseLessonMoviePageProps) => {
  const videoWrapperRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<VimeoPlayer | null>(null);

  const registrationPlayerEvent = useCallback(
    (player: VimeoPlayer) => {
      player.off('ended');
      player.off('play');

      player.on('ended', async () => {
        await onLessonCompleteEvent();
      });

      player.on('play', async () => {
        await onLessonStartEvent();
      });
    },
    [onLessonStartEvent, onLessonCompleteEvent],
  );

  useEffect(() => {
    if (!videoWrapperRef.current) return;
    if (!playerRef.current) {
      const player = new VimeoPlayer(videoWrapperRef.current, {
        ...playerDefaultOptions,
        url: vimeoEmbedUrl,
      });
      playerRef.current = player;
    }

    registrationPlayerEvent(playerRef.current);
  }, [vimeoEmbedUrl, registrationPlayerEvent]);

  return (
    <>
      <LessonTitle chapterName={chapterName} lessonName={lessonName} />
      <div
        className={styles.videoWrapper}
        style={{
          backgroundImage: `url(${BackgroundOfficeImage})`,
        }}
      >
        <iframe
          src={vimeoEmbedUrl}
          ref={videoWrapperRef}
          className={styles.videoContent}
          allowFullScreen
          allow="autoplay; fullscreen"
          title="video"
        />
        <div className={styles.buttonArea}>
          <Button onClick={onClickDownload}>動画内の資料ダウンロード</Button>
        </div>
      </div>
    </>
  );
};
